import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import HomepageV3, { type HomepageRuntimeContent, type HomepageRuntimeMenu, type HomepageRuntimePricing } from '../HomepageV3'
import { homepageV3, type HomepageV3Product } from '../config/homepageV3'
import { fetchPublishedMenu } from '../config/liveMenu'
import { fetchPricingDelivery } from '../config/livePricingDelivery'
import { formatPublishedMenuDateRange } from '../config/publishedMenuWeek'
import { publicProductTypeForFamily, type ProductFamily } from '../../shared/productFamily'
import {
  DEFAULT_PRODUCT_IMAGE_FRAMING,
  normalizeStoredProductImageFraming,
  type ProductImageFraming,
} from '../../shared/productPresentation'
import FramedProductImage from '../components/FramedProductImage'
import '../styles/admin-content-media.css'
import '../styles/order-v3.css'
import '../styles/order-v3-desktop-qa-corrections.css'
import '../styles/order-v3-responsive.css'

type HomepageFields = Partial<HomepageRuntimeContent & { weeklyMenuSupportingCopy: string }>

type Snapshot = {
  homepage: { draft: HomepageFields }
  products: Array<{
    id: string
    name: string
    productFamily: ProductFamily
    description: string
    staticImage: string
    defaultPresentation: {
      hoverBackground: string
    }
    assignment: null | {
      draftMediaId: string | null
      draftAltText: string
      draftFraming: ProductImageFraming
      draftHoverBackground: string
    }
  }>
}

type OrderPreviewProduct = {
  id: string
  name: string
  type: 'Brownie Cookie Sandwich' | 'Stuffed Cookie' | 'Brownie'
  description: string
  image: string
  framing: ProductImageFraming
  hoverBackground: string
}

const DEFAULT_CONTENT: HomepageRuntimeContent = {
  heroHeadingLineOne: homepageV3.top.headingLineOne,
  heroHeadingLineTwo: homepageV3.top.headingLineTwo,
  heroSupportingCopy: homepageV3.top.supportingCopy,
}

function asHomepageProduct(product: Awaited<ReturnType<typeof fetchPublishedMenu>>['products'][number]): HomepageV3Product & { imageAlt?: string } {
  return {
    id: product.id,
    name: product.name,
    type: product.type,
    description: product.description,
    image: product.image,
    imageAlt: product.imageAlt,
    framing: product.framing,
    enabled: true,
    displayOrder: product.displayOrder,
    isNew: false,
    clickTarget: '/order',
    hoverBackground: product.hoverBackground,
  }
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

export default function AdminContentMediaPreviewPage() {
  const params = new URLSearchParams(window.location.search)
  const previewSurface = params.get('surface') === 'order' ? 'order' : 'homepage'
  const focusedProductId = params.get('product')
  const [runtimeContent, setRuntimeContent] = useState<HomepageRuntimeContent>(DEFAULT_CONTENT)
  const [orderProducts, setOrderProducts] = useState<OrderPreviewProduct[]>([])
  const [weeklyMenu, setWeeklyMenu] = useState<HomepageRuntimeMenu>({ supportingCopy: 'Loading draft preview…', products: [] })
  const [runtimePricing, setRuntimePricing] = useState<HomepageRuntimePricing>({ prefix: '', price: 'Loading…' })
  const [dateRange, setDateRange] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const snapshotResponse = await fetch('/api/admin/content-media', { credentials: 'same-origin', cache: 'no-store' })
      if (snapshotResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/content-media/preview')
        return
      }
      if (!snapshotResponse.ok) throw new Error('Draft content could not be loaded.')
      const snapshot = await snapshotResponse.json() as Snapshot
      const [menu, pricing] = await Promise.all([fetchPublishedMenu(), fetchPricingDelivery()])
      const publishedDateRange = formatPublishedMenuDateRange(menu.week)
      if (!publishedDateRange) throw new Error('Published menu week could not be loaded.')
      setDateRange(publishedDateRange)
      const draft = snapshot.homepage.draft ?? {}

      const orderPreviewProducts = menu.products.map((product) => {
        const productState = snapshot.products.find((item) => item.id === product.id)
        const assignment = productState?.assignment
        const draftMediaId = assignment?.draftMediaId
        return {
          id: product.id,
          name: product.name,
          type: product.type,
          description: product.description,
          image: draftMediaId
            ? `/api/admin/content-media/media/${encodeURIComponent(draftMediaId)}/file`
            : productState?.staticImage ?? product.image,
          framing: normalizeStoredProductImageFraming(
            assignment?.draftFraming,
            product.framing ?? DEFAULT_PRODUCT_IMAGE_FRAMING,
          ),
          hoverBackground: assignment?.draftHoverBackground ?? product.hoverBackground,
        }
      })

      if (focusedProductId && !orderPreviewProducts.some((product) => product.id === focusedProductId)) {
        const productState = snapshot.products.find((product) => product.id === focusedProductId)
        if (productState) {
          const assignment = productState.assignment
          const draftMediaId = assignment?.draftMediaId
          orderPreviewProducts.push({
            id: productState.id,
            name: productState.name,
            type: publicProductTypeForFamily(productState.productFamily),
            description: productState.description,
            image: draftMediaId
              ? `/api/admin/content-media/media/${encodeURIComponent(draftMediaId)}/file`
              : productState.staticImage,
            framing: normalizeStoredProductImageFraming(
              assignment?.draftFraming,
              DEFAULT_PRODUCT_IMAGE_FRAMING,
            ),
            hoverBackground: assignment?.draftHoverBackground ?? productState.defaultPresentation.hoverBackground,
          })
        }
      }

      setOrderProducts(orderPreviewProducts)
      setRuntimeContent({
        heroHeadingLineOne: draft.heroHeadingLineOne || DEFAULT_CONTENT.heroHeadingLineOne,
        heroHeadingLineTwo: draft.heroHeadingLineTwo || DEFAULT_CONTENT.heroHeadingLineTwo,
        heroSupportingCopy: draft.heroSupportingCopy || DEFAULT_CONTENT.heroSupportingCopy,
      })
      const products = menu.products.map((product) => {
        const base = asHomepageProduct(product)
        const productState = snapshot.products.find((item) => item.id === product.id)
        if (!productState?.assignment) return base
        const assignment = productState.assignment
        const draftMediaId = assignment.draftMediaId
        return {
          ...base,
          image: draftMediaId
            ? `/api/admin/content-media/media/${encodeURIComponent(draftMediaId)}/file`
            : productState.staticImage,
          imageAlt: draftMediaId ? assignment.draftAltText || product.name : product.name,
          framing: normalizeStoredProductImageFraming(
            assignment.draftFraming,
            product.framing ?? DEFAULT_PRODUCT_IMAGE_FRAMING,
          ),
          hoverBackground: assignment.draftHoverBackground,
        }
      })
      setWeeklyMenu({
        products,
        supportingCopy: products.length
          ? draft.weeklyMenuSupportingCopy || homepageV3.weeklyMenu.supportingCopy
          : 'No flavours are available right now. Please check back later for the next published flavour drop.',
      })
      const lowestFee = Math.min(...pricing.delivery.bands.map((band) => band.feeCents))
      setRuntimePricing({ prefix: 'from', price: formatMoney(lowestFee) })
    } catch (loadError) {
      setDateRange(null)
      setError(loadError instanceof Error ? loadError.message : 'Draft preview is unavailable.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (error) {
    return (
      <main className="admin-content-media-preview__error">
        <strong>Draft preview unavailable</strong>
        <p>{error}</p>
        <button type="button" onClick={() => void load()}>Retry</button>
      </main>
    )
  }

  if (previewSurface === 'order') {
    const visibleProducts = focusedProductId
      ? orderProducts.filter((product) => product.id === focusedProductId)
      : orderProducts

    return (
      <div className="sg-v3 order-v3-page admin-content-media-order-preview-page">
        <main className="admin-content-media-order-preview-page__main">
          <section className="order-v3-stage admin-content-media-order-preview-page__stage">
            <div className="order-v3-shell order-v3-layout">
              <aside className="order-v3-builder admin-content-media-order-preview-page__builder-spacer" aria-hidden="true" />
              <div className="order-v3-main" aria-label="Order card draft preview">
                <div className="order-v3-product-grid">
                  {visibleProducts.map((product) => {
                    const style = {
                      '--order-v3-product-hover': product.hoverBackground,
                    } as CSSProperties
                    return (
                      <article className="order-v3-product-card" style={style} key={product.id}>
                        <div className="order-v3-product-card__media">
                          <FramedProductImage src={product.image} alt="" framing={product.framing} />
                        </div>
                        <div className="order-v3-product-card__copy">
                          <h3>{product.name}</h3>
                          <p className="order-v3-product-card__type">{product.type}</p>
                          <p className="order-v3-product-card__description">{product.description}</p>
                        </div>
                      </article>
                    )
                  })}
                </div>
                {!visibleProducts.length ? <p>That Order preview product is no longer active.</p> : null}
              </div>
            </div>
          </section>
        </main>
        <div className="admin-content-media-preview__badge" role="status">ORDER DRAFT PREVIEW · NOT LIVE</div>
      </div>
    )
  }

  return (
    <>
      <HomepageV3 weeklyMenu={weeklyMenu} runtimePricing={runtimePricing} runtimeContent={runtimeContent} dateRange={dateRange} previewMode />
      <div className="admin-content-media-preview__badge" role="status">DRAFT PREVIEW · NOT LIVE</div>
    </>
  )
}
